import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CollaborationSession } from '@/types/collaboration';
import { format, isToday, isTomorrow, isPast, isWithinInterval, addMinutes } from 'date-fns';
import { Video, Clock, CalendarDays, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface SessionCardProps {
  session: CollaborationSession;
  onJoin?: (sessionId: string) => void;
}

const SessionCard: React.FC<SessionCardProps> = ({ session, onJoin }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const getDateDisplay = () => {
    if (isToday(session.scheduledFor)) {
      return 'Today';
    } else if (isTomorrow(session.scheduledFor)) {
      return 'Tomorrow';
    } else {
      return format(session.scheduledFor, 'MMM dd, yyyy');
    }
  };
  
  const getTimeDisplay = () => {
    return format(session.scheduledFor, 'h:mm a');
  };
  
  const getSessionStatusBadge = () => {
    if (session.status === 'completed') {
      return (
        <Badge variant="outline" className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/20">
          Completed
        </Badge>
      );
    } else if (session.status === 'cancelled') {
      return (
        <Badge variant="outline" className="bg-red-500/20 text-red-500 hover:bg-red-500/20">
          Cancelled
        </Badge>
      );
    } else if (
      isWithinInterval(new Date(), {
        start: session.scheduledFor,
        end: addMinutes(session.scheduledFor, session.duration)
      })
    ) {
      return (
        <Badge variant="outline" className="bg-green-500/20 text-green-500 hover:bg-green-500/20">
          Live Now
        </Badge>
      );
    } else if (isPast(session.scheduledFor)) {
      return (
        <Badge variant="outline" className="bg-slate-500/20 text-slate-500 hover:bg-slate-500/20">
          Missed
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/20">
          Upcoming
        </Badge>
      );
    }
  };
  
  const handleJoinClick = () => {
    console.log(`Joining session with ID: ${session.id}`);
    
    if (onJoin) {
      onJoin(session.id);
    } else {
      toast({
        title: "Joining session",
        description: `Joining ${session.name} (ID: ${session.id})`
      });
      
      navigate(`/collaboration/sessions/${session.id}`);
    }
  };
  
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow animate-fade-in">
      <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm">{session.name}</CardTitle>
            {getSessionStatusBadge()}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CalendarDays className="h-3 w-3" />
            <span>{getDateDisplay()} at {getTimeDisplay()}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{session.duration} minutes</span>
          </div>
          <div className="text-xs text-muted-foreground">
            <strong>Session ID:</strong> {session.id}
          </div>
          
          <div className="mt-2">
            {session.status === 'completed' || session.status === 'Completed' ? (
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full flex items-center gap-2"
              >
                <Download className="h-3 w-3" />
                <span>Download Recording</span>
              </Button>
            ) : isWithinInterval(new Date(), {
                start: session.scheduledFor || new Date(),
                end: addMinutes(session.scheduledFor || new Date(), session.duration)
              }) ? (
              <Button 
                variant="default" 
                size="sm" 
                className="w-full flex items-center gap-2"
                onClick={handleJoinClick}
              >
                <Video className="h-3 w-3" />
                <span>Join Session</span>
              </Button>
            ) : !isPast(session.scheduledFor || new Date()) ? (
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
              >
                Add to Calendar
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SessionCard;
